import React from "react";
import {
  AuthContextProvider,
  useAuth,
  AuthContextValue,
} from "contexts/AuthContext";
import { FeatureFlagProvider } from "contexts/FeatureFlagContext";
import { Toaster } from "components/ui/sonner";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { ErrorBoundary } from "components/errors/ErrorBoundary";
import { ErrorFallback } from "components/errors/ErrorFallbackScreen";
import { RoutePending } from "components/loading";
import { useAppVersionRefresh } from "hooks/useAppVersionRefresh";
import { logError } from "shared/services/ErrorReporting";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    auth: undefined! as AuthContextValue,
  },
  // Per-match pending component: without one, an in-flight chunk suspends the
  // whole shell and tears down its Firestore listeners.
  defaultPendingComponent: RoutePending,
  // Shorter than the router's own default second; the minimum display time
  // is left at its default so the spinner cannot flash for a frame.
  defaultPendingMs: 150,
  // The ErrorBoundary sits outside RouterProvider, so a route error never reaches it.
  defaultErrorComponent: ErrorFallback,
  defaultOnCatch: (error) => logError(error),
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface RouterContext {
    auth: AuthContextValue;
  }
}

const InnerApp = () => {
  const auth = useAuth();

  return <RouterProvider router={router} context={{ auth }} />;
};

const App: React.FC = () => {
  useAppVersionRefresh();

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <AuthContextProvider>
        <FeatureFlagProvider>
          <InnerApp />
        </FeatureFlagProvider>
        <Toaster />
      </AuthContextProvider>
    </ErrorBoundary>
  );
};

export default App;
